import { motion } from "framer-motion";
import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";
import { ArrowRight, ArrowLeft } from "lucide-react";
import ironHangar from "@/assets/iron-hangar.jpg";
import ironShade from "@/assets/iron-shade.jpg";
import ironStairs from "@/assets/iron-stairs.jpg";
import ironRailing from "@/assets/iron-railing.jpg";

const IronProductDetail = () => {
  const { slug } = useParams<{ slug: string }>();
  const { language, isRTL } = useLanguage();

  const content = {
    ar: {
      backToProducts: "العودة لمنتجات الحديد",
      contactUs: "تواصل معنا للطلب",
      features: "المميزات",
      ourProjects: "مشاريعنا وأعمالنا",
      products: {
        "hangars": {
          title: "هناجر",
          description: "هناجر بمختلف المقاسات والتصميمات",
          longDescription: "نقدم هناجر حديدية بمختلف المقاسات والتصميمات لتلبية احتياجات المصانع والمستودعات والمزارع. تصاميم متينة ومقاومة للعوامل الجوية مع إمكانية التخصيص حسب المتطلبات.",
          features: ["مقاسات متنوعة", "مقاومة للعوامل الجوية", "تصاميم مخصصة", "متانة عالية", "تركيب سريع"],
          images: [ironHangar, ironShade, ironStairs]
        },
        "shades": {
          title: "مظلات",
          description: "مظلات بتصميمات عصرية ومتينة",
          longDescription: "مظلات حديدية بتصميمات عصرية ومتينة للسيارات والحدائق والمداخل. حماية ممتازة من الشمس والأمطار مع الحفاظ على المظهر الجمالي.",
          features: ["حماية من الشمس", "مقاومة للأمطار", "تصاميم عصرية", "ألوان متعددة", "صيانة سهلة"],
          images: [ironShade, ironHangar, ironRailing]
        },
        "stairs": {
          title: "سلالم حديد",
          description: "سلالم حديد بتصميمات مبتكرة",
          longDescription: "سلالم حديدية بتصميمات مبتكرة للمنازل والفلل والمباني التجارية. تجمع بين الأمان والجمال مع خيارات متعددة من التشطيبات والألوان.",
          features: ["تصاميم مبتكرة", "أمان عالي", "تشطيبات متنوعة", "مقاومة للصدأ", "متانة فائقة"],
          images: [ironStairs, ironRailing, ironShade]
        },
        "railings": {
          title: "درابزينات",
          description: "درابزينات حديد بتصميمات أنيقة",
          longDescription: "درابزينات حديدية بتصميمات أنيقة للسلالم والشرفات والأسطح. تتميز بالمتانة والجمال مع خيارات متعددة من الأنماط الكلاسيكية والعصرية.",
          features: ["تصاميم أنيقة", "أمان ممتاز", "أنماط متنوعة", "طلاء مقاوم", "عمر طويل"],
          images: [ironRailing, ironStairs, ironHangar]
        }
      }
    },
    en: {
      backToProducts: "Back to Iron Products",
      contactUs: "Contact Us to Order",
      features: "Features",
      ourProjects: "Our Projects & Work",
      products: {
        "hangars": {
          title: "Hangars",
          description: "Hangars in various sizes and designs",
          longDescription: "We offer iron hangars in various sizes and designs to meet the needs of factories, warehouses, and farms. Durable and weather-resistant designs with customization options according to requirements.",
          features: ["Various sizes", "Weather resistant", "Custom designs", "High durability", "Quick installation"],
          images: [ironHangar, ironShade, ironStairs]
        },
        "shades": {
          title: "Shades",
          description: "Modern and durable shade structures",
          longDescription: "Modern and durable iron shades for cars, gardens, and entrances. Excellent protection from sun and rain while maintaining aesthetic appearance.",
          features: ["Sun protection", "Rain resistant", "Modern designs", "Multiple colors", "Easy maintenance"],
          images: [ironShade, ironHangar, ironRailing]
        },
        "stairs": {
          title: "Iron Stairs",
          description: "Innovative iron staircase designs",
          longDescription: "Innovative iron staircases for homes, villas, and commercial buildings. Combining safety and beauty with multiple finishing and color options.",
          features: ["Innovative designs", "High safety", "Various finishes", "Rust resistant", "Superior durability"],
          images: [ironStairs, ironRailing, ironShade]
        },
        "railings": {
          title: "Railings",
          description: "Elegant iron railing designs",
          longDescription: "Elegant iron railings for stairs, balconies, and rooftops. Featuring durability and beauty with multiple classic and modern style options.",
          features: ["Elegant designs", "Excellent safety", "Various styles", "Resistant coating", "Long lifespan"],
          images: [ironRailing, ironStairs, ironHangar]
        }
      }
    }
  };

  const t = content[language];
  const product = slug ? t.products[slug as keyof typeof t.products] : null;

  if (!product) {
    return (
      <div className="min-h-screen bg-background" dir={isRTL ? "rtl" : "ltr"}>
        <Navbar />
        <div className="container mx-auto px-4 py-20 text-center">
          <h1 className="text-2xl font-bold mb-4">{language === "ar" ? "المنتج غير موجود" : "Product Not Found"}</h1>
          <Link to="/iron">
            <Button className="bg-primary hover:bg-primary/90">{t.backToProducts}</Button>
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const ArrowIcon = isRTL ? ArrowRight : ArrowLeft;

  return (
    <div className="min-h-screen bg-background" dir={isRTL ? "rtl" : "ltr"}>
      <Navbar />
      <div className="container mx-auto px-4 py-12">
        <Link to="/iron" className="inline-flex items-center gap-2 text-primary hover:text-primary/80 mb-8 font-kufi">
          <ArrowIcon className="w-4 h-4" />
          {t.backToProducts}
        </Link>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="text-center mb-12">
          <h1 className="text-4xl font-kufi font-bold text-primary mb-4">{product.title}</h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">{product.description}</p>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }} className="bg-card rounded-xl shadow-lg p-8 mb-12">
          <p className="text-lg leading-relaxed text-card-foreground">{product.longDescription}</p>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.2 }} className="bg-primary/5 rounded-xl p-8 mb-12">
          <h2 className="text-2xl font-kufi font-bold text-primary mb-6">{t.features}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {product.features.map((feature, index) => (
              <div key={index} className="flex items-center gap-3 bg-card p-4 rounded-lg shadow-sm">
                <div className="w-2 h-2 rounded-full bg-primary flex-shrink-0" />
                <span className="text-card-foreground">{feature}</span>
              </div>
            ))}
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.3 }} className="mb-12">
          <h2 className="text-2xl font-kufi font-bold text-primary mb-8 text-center">{t.ourProjects}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {product.images.map((image, index) => (
              <motion.div key={index} initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.3, delay: index * 0.1 }} className="group relative overflow-hidden rounded-xl shadow-lg aspect-[4/3]">
                <img src={image} alt={`${product.title} ${index + 1}`} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </motion.div>
            ))}
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.4 }} className="text-center bg-primary/10 rounded-xl p-8">
          <Link to="/contact">
            <Button size="lg" className="bg-primary hover:bg-primary/90 text-white font-kufi text-lg px-8">{t.contactUs}</Button>
          </Link>
        </motion.div>
      </div>
      <Footer />
    </div>
  );
};

export default IronProductDetail;
